import React, { useEffect, useState } from 'react';
import { ButtonGroup, ButtonToolbar, Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchFilter } from '../../../store/ai2';
import type { RootState } from '../../../store';
import { QuickSearchBadge } from '../Basics/QuickSearchBadge';
import { useFilterOptions, type Option } from './hooks/useFilterOptions';
import { insightMapping } from './utils/insightMapping';
import SelectFilter from './components/SelectFilter';
import InputFilter from './components/InputFilter';

type EpisodeOption = Option | null;

interface FormValues {
  clinics: Option[];
  patientName: string;
  episodeStatus: EpisodeOption;
  insightCategory: Option[];
  withingsConnected: boolean | null;
}

const AishFilters = () => {
  const dispatch = useDispatch();
  const [selectedPhrase, setSelectedPhrase] = useState<string | null>(null);
  const { allTenancyClinics, selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const { register, watch, getValues, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      clinics: [],
      patientName: '',
      episodeStatus: null,
      insightCategory: [],
      withingsConnected: null,
    },
  });

  const quickSearchPhrases: string[] =
    (selectedTenancy?.features?.quickSearchOptions as string[]) || [];
  const insightCategory =
    (selectedTenancy?.features?.insightCategory as Record<
      string,
      string | string[]
    >) || {};

  const filterOptions = useFilterOptions({
    allTenancyClinics,
    selectedTenancy,
    insightCategory,
  });

  const getFormValues = () => {
    const currentValues = getValues();
    return {
      clinics: currentValues.clinics.map((item) => item.value),
      episodeStatus: currentValues.episodeStatus?.value || '',
      insightCategory: currentValues.insightCategory.map((m) =>
        insightMapping[m.value] ? insightMapping[m.value] : m.value,
      ),
      patientName: currentValues.patientName,
      withingsConnected: currentValues.withingsConnected,
    };
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'patientName') {
      setValue('patientName', value);
    }
  };

  const filterField = (
    field: keyof FormValues,
    selectedOptions: Option[] | Option | null,
  ) => {
    if (typeof field !== 'string') {
      return;
    }
    setValue(field as any, selectedOptions as any);
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = { ...getFormValues() };
    dispatch(setSearchFilter(values));
  };

  const onResetForm = () => {
    reset();
    setSelectedPhrase(null);
    dispatch(setSearchFilter(getFormValues()));
  };

  useEffect(() => {
    reset();
  }, [selectedTenancy]);

  const handlePhraseClick = (phrase: string) => {
    onResetForm();
    const isRemove = phrase === selectedPhrase;
    const newPhrase = isRemove ? null : phrase;
    setSelectedPhrase(newPhrase);

    switch (phrase) {
      case 'Withings not connected':
        if (!isRemove) {
          setValue('withingsConnected', false);
        }
        break;
      default:
        break;
    }
    dispatch(setSearchFilter(getFormValues()));
  };

  return (
    <form className="filter-container" onSubmit={onSubmit}>
      <Container fluid className="filterBg pb-4">
        <div className="d-flex flex-wrap align-items-start w-100 px-4 pt-5 pb-4 gap-3">
          <div className="d-flex flex-column gap-1" style={{ minWidth: 240 }}>
            <SelectFilter
              label="Service"
              registerReturn={register('clinics')}
              isMulti={true}
              selectClassName="w-100"
              options={filterOptions.clinicOptions}
              value={watch('clinics')}
              placeholder="All"
              showTick={true}
              onChange={(selectedOptions) =>
                filterField('clinics', selectedOptions as Option[])
              }
              onClear={() => {
                setValue('clinics', []);
              }}
            />
          </div>

          <div className="d-flex flex-column gap-1" style={{ minWidth: 240 }}>
            <SelectFilter
              label="Insight category"
              registerReturn={register('insightCategory')}
              isMulti={true}
              selectClassName="w-100"
              options={filterOptions.fcOptions}
              value={watch('insightCategory')}
              placeholder="All"
              showTick={true}
              onChange={(selectedOptions) =>
                filterField('insightCategory', selectedOptions as Option[])
              }
              onClear={() => {
                setValue('insightCategory', []);
              }}
            />
          </div>

          <div className="d-flex flex-column gap-1" style={{ minWidth: 240 }}>
            <SelectFilter
              label="Episode status"
              registerReturn={register('episodeStatus')}
              isMulti={false}
              selectClassName="w-100"
              isClearable={true}
              options={filterOptions.esOptions}
              value={watch('episodeStatus')}
              placeholder="All"
              showTick={true}
              onChange={(selectedOptions) =>
                filterField('episodeStatus', selectedOptions as Option | null)
              }
              onClear={() => {
                setValue('episodeStatus', null);
              }}
            />
          </div>

          <div className="d-flex flex-column gap-1" style={{ minWidth: 240 }}>
            <InputFilter
              label="Patient name"
              registerReturn={register('patientName')}
              inputProps={{
                type: 'text',
                placeholder: '',
                name: 'patientName',
                id: 'patientName',
              }}
              onChange={onInputChange}
            />
          </div>

          <div className="filterItem">
            <ButtonToolbar className="filterBtns">
              <ButtonGroup className="me-2">
                <button
                  type="submit"
                  className="btn btnPrimary"
                  style={{ minWidth: 110 }}
                >
                  Show results
                </button>
              </ButtonGroup>
              <ButtonGroup>
                <button
                  type="button"
                  className="btn btnSecondary"
                  onClick={onResetForm}
                >
                  Clear
                </button>
              </ButtonGroup>
            </ButtonToolbar>
          </div>
        </div>
        {quickSearchPhrases?.length > 0 && (
          <div className="quickSearch">
            <span className="quickSearchTitle">Quick searches:</span>
            {quickSearchPhrases.map((phrase, index) => (
              <QuickSearchBadge
                key={index + '' + phrase}
                text={phrase}
                quickSearchSelected={phrase === selectedPhrase}
                onClick={() => handlePhraseClick(phrase)}
              />
            ))}
          </div>
        )}
      </Container>
    </form>
  );
};

export default AishFilters;
